import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Ruler as RulerIcon, Ruler, AlertCircle, RefreshCw, Loader2, History, Upload } from 'lucide-react';
import { StatsCard, TamanhoCard, TAMANHO_COLORS } from './StatsCard';
import { SearchInput } from './SearchInput';
import { TamanhoTable } from './TamanhoTable';
import { HistoryModal } from './HistoryModal';
import { ImportModal } from './ImportModal';
import { fetchTamanhos, batchUpdateTamanhos, fetchTamanhoHistory } from './api';
import { TamanhoSummary, HistoryEntry, TAMANHO_UL_DROPDOWN_OPTIONS } from './types';

const TAMANHO_DISPLAY_OPTIONS = Array.from(TAMANHO_UL_DROPDOWN_OPTIONS);

const TamanhoScreen: React.FC = () => {
  const [tamanhos, setTamanhos] = useState<TamanhoSummary[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedTamanho, setSelectedTamanho] = useState<string | null>(null);

  const loadTamanhos = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchTamanhos();
      setTamanhos(data);
    } catch {
      setLoadError('Erro ao carregar os tamanhos. Verifique a conexao e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTamanhos();
  }, [loadTamanhos]);

  const totalCnpjs = useMemo(
    () => tamanhos.reduce((sum, t) => sum + t.totalCnpjs, 0),
    [tamanhos]
  );

  const tamanhoDistribution = useMemo(() => {
    const dist: Record<string, number> = {};
    TAMANHO_DISPLAY_OPTIONS.forEach((o) => { dist[o] = 0; });
    tamanhos.forEach((t) => {
      if (dist[t.tamanhoUL] !== undefined) {
        dist[t.tamanhoUL] = t.totalCnpjs;
      }
    });
    return dist;
  }, [tamanhos]);

  const filteredTamanhos = useMemo(() => {
    let result = tamanhos;

    if (selectedTamanho) {
      result = result.filter((t) => t.tamanhoUL === selectedTamanho);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((t) => t.tamanhoUL.toLowerCase().includes(term));
    }

    return result;
  }, [tamanhos, searchTerm, selectedTamanho]);

  const defaultTamanhoColor = TAMANHO_COLORS['Não Informado'];

  const handleOpenHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const entries = await fetchTamanhoHistory();
      setHistoryData(entries);
    } catch {
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleImportBatch = async (items: { cnpjlojista: string; tamanho_ul: string }[]) => {
    if (items.length > 0) {
      await batchUpdateTamanhos(items);
    }
  };

  const handleImportClose = () => {
    setImportOpen(false);
    loadTamanhos();
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <header className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Tamanhos de Loja
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Gerencie os Tamanhos UL dos Lojistas
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={loadTamanhos}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
                  aria-label="Recarregar dados"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setImportOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Importar</span>
                </button>
                <button
                  onClick={handleOpenHistory}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">Historico</span>
                </button>
              </div>
            </div>
          </header>

          <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 mb-4">
            <StatsCard
              title="Total CNPJs"
              value={totalCnpjs}
              description="Lojistas cadastrados"
              icon={<RulerIcon className="w-5 h-5" />}
            />
            <StatsCard
              title="Categorias"
              value={tamanhos.length}
              description="Tamanhos distintos"
              icon={<Ruler className="w-5 h-5" />}
            />
          </div>

          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 mb-8">
            {TAMANHO_DISPLAY_OPTIONS.map((tamanho) => (
              <TamanhoCard
                key={tamanho}
                name={tamanho}
                count={tamanhoDistribution[tamanho] ?? 0}
                total={totalCnpjs}
                color={TAMANHO_COLORS[tamanho] ?? defaultTamanhoColor}
                icon={<Ruler className="w-4 h-4" />}
                active={selectedTamanho === tamanho}
                onClick={() => setSelectedTamanho((prev) => prev === tamanho ? null : tamanho)}
              />
            ))}
          </div>

          <div className="mb-5">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar por tamanho..."
            />
          </div>

          {loadError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex flex-col items-center gap-3 mb-5">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <p className="text-sm text-red-700 text-center">{loadError}</p>
              <button
                onClick={loadTamanhos}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Tentar novamente
              </button>
            </div>
          )}

          {isLoading && !loadError && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <p className="text-sm text-gray-500">Carregando tamanhos...</p>
            </div>
          )}

          {!isLoading && !loadError && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <TamanhoTable data={filteredTamanhos} />
            </div>
          )}
        </div>
      </div>

      <HistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={historyData}
        loading={historyLoading}
      />

      <ImportModal
        isOpen={importOpen}
        onClose={handleImportClose}
        onImportComplete={handleImportBatch}
      />
    </>
  );
};

export default TamanhoScreen;
